import React, { useEffect, useMemo, useState } from "react";

import { CalendarOutlined, ClockCircleOutlined, GlobalOutlined, UserOutlined } from "@ant-design/icons";
import { ProfileModel } from "@common/domain/models/Profile";
import { getAvailableSlots, getSlotBooking, IAvailableSlotsPayload, IGetSlotBookingPayload } from "@common/redux/slot-booking/actions";
import { connect } from "react-redux";
import moment from "moment";
import { Button, Input } from "antd";
import { TextAreaInput } from "@components/Input";
import { createEvent, slotBooking } from "@common/redux/meeting/actions";
import { ICreateEvent } from "@common/redux/meeting/interface";
import { createEventErrorSelector, createEventLoaderSelector, createEventSelector } from "@common/redux/meeting/selectors";
import Navigator from "@navigator/index";
import { NavigationUrl, URLs } from "@navigator/NavigationUrl";
import { translate } from "@translations/Translater";
import { usePrevious } from "@utils/CustomHooks";
import { getTimeZone } from "@utils/DeviceInfoUtils";
import { StorageKeys } from "@utils/LocalStorage";
import { useTranslation } from "react-i18next";
import DateCalendar from "./DateCalendar";
import { CalenderComponentTranslations } from "./message";
import { PrimaryButton } from "@components/Button";
import { displayErrorMessage } from "@utils/DisplayMessageUtility";
import { DATE_FORMAT_6 } from "@utils/DateHelper";
import { IAvailableSlots, ISlotBooking } from "@common/domain/models/SlotBookingModel";
import { i18n } from "@translations/i18n";

import './styles.less';

interface IProps {
    profile: ProfileModel;
    getAvailableSlots: (payload: IAvailableSlotsPayload) => void;
    availableSlots: IAvailableSlots;
    createMeetingEvent: (payload: ICreateEvent) => void;
    eventDetail: any;
    eventDetailLoading: boolean;
    eventDetailError: string; 
    fetchSlotBooking: (payload: IGetSlotBookingPayload) => void;
    slotBooking: ISlotBooking[];
}

const SlotTiming = ({availability, title, onSlotSelect}: {
    availability: {startTime: string, endTime: string}[],
    title: string,
    onSlotSelect: (slot: any) => void
}) => (
    availability?.length ? <div className="slot-timing">
         <span>{title}</span>
        <div className="slots">
        {
            availability?.map((item: any, index: number) => {
                return (
                    <Button key={`slot-${index}`} className="slot" onClick={() => onSlotSelect(item)}>
                        {item?.startTime}
                    </Button>
                )
            })
        }
        </div>
    </div> : null
)


const CalendarComponent = (props: IProps) => {
    const {slotBooking,fetchSlotBooking} = props;
    const {t} = useTranslation()
    const [selectedDate, setSelectedDate] = useState(moment().add(1, 'days').format(DATE_FORMAT_6));
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        notes: "",
    });
    const { profile, eventDetailError } = props;
    const mentorDetails = useMemo(() => ({
        name: `${profile?.getFirstName()} ${profile?.getLastname()}`,
        email: profile?.getEmail(),
        timezone: profile?.getLocation()?.timezoneName,
    }), [profile]);

    const calUrl = useMemo(() => {
        return profile?.getCalcomUrl();
    }, [profile]);

    const userResponse = JSON.parse(localStorage.getItem(StorageKeys.USER));

    useEffect(() => {
        if (profile && selectedDate) {
            props.getAvailableSlots({ userId: profile?.getUserId(), date: selectedDate });
        }
    }, [profile, selectedDate])

      useEffect(() => {
            if (profile?.getUserId()) {
                const userId = profile.getUserId();
                fetchSlotBooking({ userId });
            }
        }, [profile])
    
    const onDateSelect = (date: string) => {
        setIsLoading(true);
        setSelectedDate(date);
    }

    const onSlotSelect = (slot: any) => {
        setSelectedSlot(slot);
        setFormData((formData) => ({
            ...formData,
            name: localStorage.getItem(StorageKeys.USER_NAME),
            email: userResponse?.userValue?.filteredEmail,
        }))
    }

    const onChangeFormData = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        })
    }

    const onBackClick = () => {
        setSelectedSlot(null);
    }

    const dateDay = useMemo(() => {
        return moment(selectedDate).format("Do MMM, YY");
    }, [selectedDate])

    const meetingDetails = useMemo(() => {
        if (selectedSlot && selectedDate) {
            return `${selectedSlot.startTime} - ${selectedSlot.endTime} ${moment(selectedDate).format("a")}`;
        }
    }, [selectedDate, selectedSlot])

    const onSubmitForm = (e) => {
        e.preventDefault()
        const [startHour, startMinutes] = selectedSlot?.startTime.split(":");
        const [endHour, endMinutes] = selectedSlot?.endTime.split(":");
        const startDate = moment(selectedDate).startOf('day').add(startHour, "hours").add(startMinutes, "minutes").toISOString();
        const endDate = moment(selectedDate).startOf('day').add(endHour, "hours").add(endMinutes, "minutes").toISOString();

        const payload: any = {
            "title": `Meeting between ${mentorDetails?.name} and ${formData?.name}`,
            "description": formData?.notes,
            "location": "",
            "start": {
                "datetime": startDate,
            },
            "end": {
                "datetime": endDate,
            },
            "status": "confirmed",
            "organizer": {
                "userId": profile?.getUserId(),
            },
            "attendees": [
                {
                    "userId": userResponse?.userValue?.filteredUserId,
                    "organizer": false,
                    "email": userResponse?.userValue?.filteredEmail,
                },
                {
                    "userId": profile?.getUserId(),
                    "organizer": true,
                    "email": profile?.getEmail(),
                }
            ],
            "clientName": "WO",
            "isZoomMeeting": true,
            "duration": props.availableSlots?.slotDuration,
            "tags": {
                "isSlotBooking": true
            },
        }
        props.createMeetingEvent(payload);
    }
    
    const eventDetailLoadingPrev = usePrevious(props.eventDetailLoading)
    useEffect(() => {
        if (eventDetailLoadingPrev && !props.eventDetailLoading && eventDetailError) {
            displayErrorMessage(eventDetailError);
        } else if (eventDetailLoadingPrev && !props.eventDetailLoading && !eventDetailError) {
            Navigator.push(NavigationUrl.generate(URLs.meetingDetail, {
                meetingId: props.eventDetail?.getId(),
            }))
        }
    }, [props.eventDetailLoading, props.eventDetail])


    useEffect(() => {
        setIsLoading(false);
    }, [props.availableSlots])

    const availability = useMemo(() => {
        return props.availableSlots?.slots?.reduce((acc, curr) => {
            const [hour, minute] = curr.startTime.split(":").map(Number);
    
            if (hour < 12) {
                acc.morning.push(curr);
            } else if (hour < 16) {
                acc.afternoon.push(curr);
            } else {
                acc.evening.push(curr);
            }
    
            return acc;
        }, { morning: [], afternoon: [], evening: [] });
    }, [props.availableSlots])

    const getUTCOffset = useMemo(() => {
        const timezone = parseFloat(getTimeZone())
        return timezone > 0 ? `UTC+${timezone}` : `UTC${timezone}`
    }, [])

    return (
        <div>
            <div className="cal-header">
                {translate(t, CalenderComponentTranslations.bookSession)}
            </div>
            <div className="cal-container">
                {!selectedSlot && <div className="cal-container-left">
                    <DateCalendar onDateSelect={onDateSelect} selectedDate={selectedDate} slotBooking={slotBooking}/>
                </div>}
                <div className="cal-container-right">
                    <div className="cal-left">
                        <div className="user-profile">
                            <UserOutlined style={{color: "#A0A0A0"}} />
                            <span>{translate(t, CalenderComponentTranslations.mentor)}: {mentorDetails?.name}</span>
                        </div>
                        {!selectedSlot && <div className="duration">
                            <ClockCircleOutlined style={{color: "#A0A0A0"}} /> 
                            <span>{translate(t, CalenderComponentTranslations.slotDuration)}: {props?.availableSlots?.slotDuration && `${props?.availableSlots?.slotDuration}mins`}</span>
                        </div>}
                        {selectedSlot && <div className="duration">
                            <CalendarOutlined style={{color: "#A0A0A0"}} />
                            <span>{translate(t, CalenderComponentTranslations.sessionDate)}: {dateDay}</span>
                        </div>}
                        {!selectedSlot && <div className="location">
                            <GlobalOutlined style={{color: "#A0A0A0"}} />
                            <span>{translate(t, CalenderComponentTranslations.myTimezone)}: {Intl.DateTimeFormat().resolvedOptions().timeZone} ({getUTCOffset})</span>
                        </div>}
                        {selectedSlot && <div className="location">
                            <GlobalOutlined style={{color: "#A0A0A0"}} />
                            <span>{translate(t, CalenderComponentTranslations.sessionTime)}: {meetingDetails}</span>
                        </div>}
                    </div>
                    
                    {!selectedSlot && <div className="cal-right">
                        <div className="slots-header">
                            {translate(t, CalenderComponentTranslations.slotsAvailable)}
                        </div>
                        <div className="slots-container">
                            {isLoading ? <div className="loading">{i18n.t('global.actions.LOADING')}</div> :
                            <>
                                {
                                    props?.availableSlots?.slots?.length === 0 && <div className="no-slots">{translate(t, CalenderComponentTranslations.noSlots)}</div>
                                }
                                <SlotTiming availability={availability?.morning} title={translate(t, CalenderComponentTranslations.morning)} onSlotSelect={onSlotSelect} />
                                <SlotTiming availability={availability?.afternoon} title={translate(t, CalenderComponentTranslations.afternoon)} onSlotSelect={onSlotSelect} />
                                <SlotTiming availability={availability?.evening} title={translate(t, CalenderComponentTranslations.evening)} onSlotSelect={onSlotSelect} />
                            </>}
                        </div>
                    </div>}
                </div>
                {
                    selectedSlot && <form className="contact-form" onSubmit={onSubmitForm}>
                        <div className="contact-info">
                            <div className="input">
                                <label>{translate(t, CalenderComponentTranslations.youName)}*</label>
                                <Input name="name" value={formData?.name} placeholder={translate(t, CalenderComponentTranslations.enterName)} onChange={onChangeFormData} />
                            </div>
                            <div className="input">
                                <label>{translate(t, CalenderComponentTranslations.yourEmail)}*</label>
                                <Input name="email" value={formData?.email} placeholder={translate(t, CalenderComponentTranslations.enterEmail)} onChange={onChangeFormData} />
                            </div>
                            <div className="input">
                                <label>{translate(t, CalenderComponentTranslations.additionalNotes)}</label>
                                <TextAreaInput name="notes" value={formData?.notes} placeholder={translate(t, CalenderComponentTranslations.enterNotes)} rows={3} autoSize={{ minRows: 3, maxRows: 6 }} onChange={onChangeFormData} />
                            </div>
                            <div className="footer">
                                <Button className="btn-cancel" onClick={onBackClick}>{translate(t, CalenderComponentTranslations.back)}</Button>
                                <PrimaryButton htmlType="submit" className="btn-primary">{translate(t, CalenderComponentTranslations.book)}</PrimaryButton>
                            </div>
                        </div>
                    </form>
                }
            </div>
        </div>
    )
};

const mapStateToProps = (state: any) => ({
    availableSlots: state.slotBooking.availableSlots,
    eventDetail: createEventSelector(state),
    eventDetailLoading: createEventLoaderSelector(state),
    eventDetailError: createEventErrorSelector(state),
    slotBooking: state.slotBooking?.slotBooking,
});

const mapDispatchToProps = (dispatch: any) => {
    return {
        getAvailableSlots: (payload: IAvailableSlotsPayload) => dispatch(getAvailableSlots(payload)),
        createMeetingEvent: (event: ICreateEvent) => dispatch(createEvent(event)),
        fetchSlotBooking: (payload: IGetSlotBookingPayload) => dispatch(getSlotBooking(payload)),
    }
};

export default (connect(mapStateToProps, mapDispatchToProps)(CalendarComponent));
